import RetroMusicGenerator from './RetroMusicGenerator.js';
import SfxManager from './SfxManager.js';
import Debug from '../utils/Debug.js';

export default class AudioManager {
    /**
     * Normalize a position input to [x,y,z] array.
     * Accepts both [x,y,z] arrays and {x,y,z} objects.
     */
    _toVec3(v) {
        if (!v) return null;
        if (Array.isArray(v) || ArrayBuffer.isView(v)) {
            const x = Number(v[0]), y = Number(v[1]), z = Number(v[2]);
            if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) return [x, y, z];
        }
        if (typeof v === 'object') {
            const x = Number(v.x), y = Number(v.y), z = Number(v.z);
            if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) return [x, y, z];
        }
        return null;
    }

    /**
     * Transform a world position into screen-aligned coordinates for the PannerNode.
     * Maps screen-right to +X and screen-depth to -Z in the default listener space.
     * This ensures screen-space panning without relying on listener orientation.
     */
    _screenAlignedPos(x, y, z) {
        const lp = this.listenerPos;
        if (!lp) return [x, y, z];
        const { sin, cos } = this._cameraRot;
        const relX = x - lp[0];
        const relZ = z - lp[2];
        return [
            lp[0] + relX * cos + relZ * sin,
            y,
            lp[2] + relX * sin - relZ * cos
        ];
    }

    constructor() {
        // Music generator and shared AudioContext (owned by FantasyMusicGenerator)
        this.musicGenerator = null;
        this.audioContext = null;
        this.masterGain = null;

        this.isInitialized = false;
        this.pendingStart = false;

        // Volumes
        this.musicVolume = 0.3;
        // UI exposes SFX volume as 0..1; we keep this as a linear scalar applied once
        // via the SFX master bus, so 0.5 is already a strong, audible level relative
        // to musicVolume = 0.3.
        this.sfxVolume = 0.5;
        this.sfxMuted = false;

        // Additional per-bus scalar for UI SFX (relative to global SFX volume).
        // 1.0 = authored UI loudness; >1 = boost, 0 = mute UI while leaving world SFX.
        this.uiGain = 1.0;

        // SFX buses (created lazily once the music AudioContext is ready)
        this.sfxMasterGain = null;
        this.uiBusGain = null;
        this.worldSfxGain = null;
        this.ambienceGain = null;

        // SFX helper (buffer loading and one-shots)
        this.sfx = null;

        // World SFX mix (per-category gain and distance multipliers; driven by Settings Menu)
        this.mix = {
            playerFootstepGain: 1.0,
            npcFootstepGain: 1.0,
            combatGain: 1.0,
            npcVoiceGain: 1.0,
            fireGain: 1.0,
            footstepDistanceMul: 1.0,
            combatDistanceMul: 1.0,
            npcVoiceDistanceMul: 1.0,
            fireDistanceMul: 1.0
        };

        // Looping fire sources (handheld torch + world fireplaces)
        this._torchLoop = null;
        this._torchLoopRequestToken = 0;
        this._fireplaceLoops = new Map();
        // Per-fireplace async creation tokens to avoid leaked loops when multiple
        // _createFireLoop requests race in flight for the same light id.
        this._fireplaceLoopTokens = new Map();

        // Looping water movement sources (per-character swim / underwater beds).
        // Keyed by logical character id ("player", NPC id, etc.).
        this._waterLoopById = new Map();   // id -> { loop, state: 'surface'|'underwater'|'none' }
        this._waterCharState = new Map();  // id -> { wasInWater, lastSplashTime, state }

        // Global weather ambience (non-positional loops) driven by WeatherSystem.
        // We treat wind and rain as separate layers so they can overlap and crossfade independently.
        this._weatherWindLoop = null;
        this._weatherRainLoop = null;
        this._weatherWindKind = null; // 'light'|'strong'|null
        this._weatherRainKind = null; // 'light'|'heavy'|null
        this._weatherWindToken = 0;
        this._weatherRainToken = 0;
        this._weatherTarget = {
            windGain: 0,
            rainGain: 0
        };

        // When the listener is underwater, exterior ambience (wind/rain/biomes/etc.) should
        // rapidly fade out. This is applied as a multiplier on the ambience bus.
        // 0 = fully audible exterior, 1 = fully muted exterior.
        this._exteriorAmbienceOcclusion = 0;

        // Last known listener position in world space; used for manual
        // distance attenuation and hard range culling on top of PannerNode.
        // Starts as null so we never gate audio against a bogus (0,0,0) origin.
        this.listenerPos = null;

        // Camera rotation (sin/cos) for transforming world positions into
        // screen-aligned coordinates before passing to PannerNode.
        // This avoids relying on listener orientation (which has browser quirks).
        this._cameraRot = { sin: 0, cos: 1 };
 
        this._ctxGestureHooked = false;
    }

    _stopGenericLoop(loopObj, fadeSeconds = 0.25) {
        if (!loopObj || !this.audioContext) return;
        const ac = this.audioContext;
        try {
            const now = ac.currentTime;
            const g = loopObj.envelope && loopObj.envelope.gain;
            if (g && typeof g.cancelScheduledValues === 'function') {
                const current = g.value;
                g.cancelScheduledValues(now);
                g.setValueAtTime(current, now);
                g.linearRampToValueAtTime(0.0001, now + Math.max(0.01, fadeSeconds));
            }
            if (loopObj.source && typeof loopObj.source.stop === 'function') {
                loopObj.source.stop(now + Math.max(0.02, fadeSeconds) + 0.03);
            }
        } catch {
            try { if (loopObj.source && typeof loopObj.source.stop === 'function') loopObj.source.stop(); } catch {}
        }
    }

    /**
     * Set how much to occlude (mute) *exterior* ambience due to being underwater.
     * This is applied to the ambience bus so it affects all current/future ambient layers.
     *
     * @param {number} occlusion01 - 0..1 (0 = no occlusion, 1 = full mute)
     */
    setExteriorAmbienceOcclusion(occlusion01) {
        const v = Math.max(0, Math.min(1, Number(occlusion01) || 0));
        this._exteriorAmbienceOcclusion = v;

        if (!this.audioContext || !this.ambienceGain) return;

        // Nonlinear curve so the last part of the fade happens quickly.
        const mul = Math.pow(1.0 - v, 1.8);
        try {
            const t = this.audioContext.currentTime;
            const g = this.ambienceGain.gain;
            if (typeof g.cancelScheduledValues === 'function' && typeof g.setTargetAtTime === 'function') {
                g.cancelScheduledValues(t);
                g.setTargetAtTime(mul, t, 0.06);
            } else {
                g.value = mul;
            }
        } catch {
            try { this.ambienceGain.gain.value = mul; } catch {}
        }
    }

    _setLoopGain(loopObj, targetGain, tau = 0.15) {
        if (!loopObj || !this.audioContext || !loopObj.envelope) return;
        const ac = this.audioContext;
        const g = loopObj.envelope.gain;
        const v = Math.max(0, Number(targetGain) || 0);
        try {
            const now = ac.currentTime;
            if (typeof g.cancelScheduledValues === 'function' && typeof g.setTargetAtTime === 'function') {
                g.cancelScheduledValues(now);
                g.setTargetAtTime(v, now, Math.max(0.02, Number(tau) || 0.15));
            } else {
                g.value = v;
            }
        } catch {
            try { g.value = v; } catch {}
        }
    }

    async _createAmbientLoop(url, busNode, initialGain = 1.0) {
        if (!this._ensureSfxReady()) return null;
        if (!this.audioContext || !this.sfx || !busNode) return null;

        const ac = this.audioContext;
        const buffer = await this.sfx.loadBuffer(url);
        if (!buffer) return null;

        let source = null;
        let envelope = null;
        try {
            source = ac.createBufferSource();
            envelope = ac.createGain();

            source.buffer = buffer;
            source.loop = true;

            const now = ac.currentTime;
            const startTime = now + 0.01;
            const attack = 0.35;
            const g0 = 0.0;
            const g1 = Math.max(0, Number(initialGain) || 0);

            envelope.gain.setValueAtTime(g0, startTime);
            envelope.gain.linearRampToValueAtTime(Math.max(0.0001, g1), startTime + attack);

            source.connect(envelope);
            envelope.connect(busNode);
            source.start(startTime);

            return { source, envelope, url };
        } catch (e) {
            try { if (source) source.stop(); } catch {}
            return null;
        }
    }

    /**
     * Update global weather ambience (wind + rain loops). Intended to be called every frame
     * (or at least regularly) by the WeatherSystem.
     *
     * meta: {
     *   enabled?: boolean,
     *   weatherType?: string, // 'Clear'|'Overcast'|'Rain'|'Storm'|'Snow'|'Blizzard'
     *   intensity?: number,   // 0..1
     *   windSpeed?: number    // m/s-ish
     * }
     */
    updateWeatherAmbience(meta = {}) {
        const enabled = (meta && meta.enabled !== undefined) ? !!meta.enabled : true;

        const ready = this._ensureSfxReady();
        if (!enabled || !ready || this.sfxMuted || this.sfxVolume <= 0) {
            // Hard stop and invalidate any in-flight loop creations.
            this._weatherWindToken++;
            this._weatherRainToken++;
            if (this._weatherWindLoop) {
                this._stopGenericLoop(this._weatherWindLoop, 0.35);
                this._weatherWindLoop = null;
            }
            if (this._weatherRainLoop) {
                this._stopGenericLoop(this._weatherRainLoop, 0.35);
                this._weatherRainLoop = null;
            }
            this._weatherWindKind = null;
            this._weatherRainKind = null;
            this._weatherTarget.windGain = 0;
            this._weatherTarget.rainGain = 0;
            return;
        }

        // Inputs
        const type = String(meta.weatherType || meta.type || '');
        const intensity = Math.max(0, Math.min(1, Number(meta.intensity) || 0));
        const windSpeed = Math.max(0, Number(meta.windSpeed) || 0);

        const isRain = (type === 'Rain' || type === 'Storm');
        const stormy = (type === 'Storm' || type === 'Blizzard');

        // --- Decide desired rain loop kind and target gain ---------------------------------
        let desiredRainKind = null;
        let targetRainGain = 0;
        if (isRain && intensity > 0.02) {
            // Hysteresis around the heavy threshold to avoid chatter.
            const prev = this._weatherRainKind;
            if (prev === 'heavy') {
                desiredRainKind = (intensity < 0.55) ? 'light' : 'heavy';
            } else {
                desiredRainKind = (intensity > 0.65) ? 'heavy' : 'light';
            }

            // Louder in storms.
            const curve = Math.pow(intensity, 0.85);
            const base = (desiredRainKind === 'heavy') ? 0.95 : 0.65;
            const stormBoost = stormy ? 1.08 : 1.0;
            targetRainGain = base * curve * stormBoost;
        }

        // --- Decide desired wind loop kind and target gain ---------------------------------
        let desiredWindKind = null;
        let targetWindGain = 0;
        {
            // Treat storminess as additional perceived wind strength.
            const perceived = windSpeed + (stormy ? (2.0 + 4.0 * intensity) : 0);

            // Allow true calm to be silent.
            const calmGate = stormy ? 0.25 : 0.85;
            if (perceived >= calmGate) {
                const prev = this._weatherWindKind;
                if (prev === 'strong') {
                    desiredWindKind = (perceived < 5.5) ? 'light' : 'strong';
                } else {
                    desiredWindKind = (perceived > 6.5) ? 'strong' : 'light';
                }

                const speed01 = Math.max(0, Math.min(1, perceived / 12.0));
                const base = (desiredWindKind === 'strong') ? 0.75 : 0.55;
                // Keep a small audible bed under sustained wind.
                const floor = stormy ? 0.08 : 0.04;
                targetWindGain = Math.max(floor, base * speed01);
            }
        }

        // Store targets so late-resolving async loop creation adopts the current gain.
        this._weatherTarget.windGain = targetWindGain;
        this._weatherTarget.rainGain = targetRainGain;

        const bus = this.ambienceGain || this.worldSfxGain || this.sfxMasterGain;

        // --- Wind loop management ----------------------------------------------------------
        if (!desiredWindKind) {
            this._weatherWindToken++;
            if (this._weatherWindLoop) {
                this._stopGenericLoop(this._weatherWindLoop, 0.5);
                this._weatherWindLoop = null;
            }
            this._weatherWindKind = null;
        } else {
            const url = (desiredWindKind === 'strong')
                ? 'audio/sfx/ambient/amb_wind_strong_01.wav'
                : 'audio/sfx/ambient/amb_wind_light_01.wav';

            if (!this._weatherWindLoop || this._weatherWindKind !== desiredWindKind) {
                // Invalidate previous creations and fade out any current loop.
                const token = (this._weatherWindToken = (this._weatherWindToken || 0) + 1);
                if (this._weatherWindLoop) {
                    this._stopGenericLoop(this._weatherWindLoop, 0.45);
                    this._weatherWindLoop = null;
                }
                this._weatherWindKind = desiredWindKind;

                this._createAmbientLoop(url, bus, Math.max(0.0001, this._weatherTarget.windGain))
                    .then(loop => {
                        if (!loop) return;
                        if (this._weatherWindToken !== token) {
                            try { this._stopGenericLoop(loop, 0.2); } catch {}
                            return;
                        }
                        this._weatherWindLoop = loop;
                        // Snap/settle to current target.
                        this._setLoopGain(loop, this._weatherTarget.windGain, 0.12);
                    })
                    .catch(() => {});
            } else {
                // Same kind; just update gain.
                this._setLoopGain(this._weatherWindLoop, targetWindGain, 0.18);
            }
        }

        // --- Rain loop management ----------------------------------------------------------
        if (!desiredRainKind) {
            this._weatherRainToken++;
            if (this._weatherRainLoop) {
                this._stopGenericLoop(this._weatherRainLoop, 0.55);
                this._weatherRainLoop = null;
            }
            this._weatherRainKind = null;
        } else {
            const url = (desiredRainKind === 'heavy')
                ? 'audio/sfx/ambient/amb_rain_heavy_01.wav'
                : 'audio/sfx/ambient/amb_rain_light_01.wav';

            if (!this._weatherRainLoop || this._weatherRainKind !== desiredRainKind) {
                const token = (this._weatherRainToken = (this._weatherRainToken || 0) + 1);
                if (this._weatherRainLoop) {
                    this._stopGenericLoop(this._weatherRainLoop, 0.55);
                    this._weatherRainLoop = null;
                }
                this._weatherRainKind = desiredRainKind;

                this._createAmbientLoop(url, bus, Math.max(0.0001, this._weatherTarget.rainGain))
                    .then(loop => {
                        if (!loop) return;
                        if (this._weatherRainToken !== token) {
                            try { this._stopGenericLoop(loop, 0.25); } catch {}
                            return;
                        }
                        this._weatherRainLoop = loop;
                        this._setLoopGain(loop, this._weatherTarget.rainGain, 0.12);
                    })
                    .catch(() => {});
            } else {
                this._setLoopGain(this._weatherRainLoop, targetRainGain, 0.18);
            }
        }
    }

    async initialize(seed = Math.floor(Math.random() * 10000), opts = {}) {
        if (this.isInitialized) return;
        const config = {
            seed,
            volume: this.musicVolume,
            tempo: this._tempoFromSeed(seed),
            key: opts.key || 'A',
            scale: opts.scale || this._scaleFromSeed(seed)
        };
        this.musicGenerator = new RetroMusicGenerator(config);
        this.musicGenerator.setAutoMoodMode(true);

        // Attach to the music generator's AudioContext/masterGain and create SFX buses.
        this._initSfxBuses();

        this.isInitialized = true;
        if (this.pendingStart) {
            await this.startMusic();
            this.pendingStart = false;
        }
        this._bindUserGestureResume();
    }

    _bindUserGestureResume() {
        if (this._ctxGestureHooked || !this.musicGenerator || !this.musicGenerator.audioContext) return;
        this._ctxGestureHooked = true;
        const ac = this.musicGenerator.audioContext;
        const events = ['pointerdown', 'touchstart', 'mousedown', 'keydown'];
        const handler = async () => {
            try {
                if (ac.state === 'suspended') await ac.resume();
                // Once running, remove all gesture listeners to stop creating
                // unnecessary promises on every user interaction for the session lifetime
                if (ac.state === 'running') {
                    events.forEach(ev => window.removeEventListener(ev, handler));
                }
            } catch {}
        };
        events.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
    }

    /**
     * Attach SFX buses to the existing FantasyMusicGenerator audio graph.
     * This reuses the same AudioContext and masterGain as the procedural music.
     */
    _initSfxBuses() {
        if (!this.musicGenerator || !this.musicGenerator.audioContext || !this.musicGenerator.masterGain) return;
        if (this.sfxMasterGain) return; // already initialized

        const ac = this.musicGenerator.audioContext;
        const master = this.musicGenerator.masterGain;

        this.audioContext = ac;
        this.masterGain = master;

        // Shared SFX master (independent of musicVolume so SFX are not muted when music is 0)
        this.sfxMasterGain = ac.createGain();
        this.sfxMasterGain.gain.value = this.sfxMuted ? 0 : this.sfxVolume;
 
        // Logical buses for future expansion (world SFX / ambience / etc.)
        this.uiBusGain = ac.createGain();
        // Apply per-UI scalar on top of global SFX volume.
        this.uiBusGain.gain.value = (this.uiGain != null ? this.uiGain : 1.0);
        this.worldSfxGain = ac.createGain();
        this.ambienceGain = ac.createGain();

        // Apply any already-known underwater occlusion to the ambience bus.
        try { this.setExteriorAmbienceOcclusion(this._exteriorAmbienceOcclusion); } catch {}

        this.uiBusGain.connect(this.sfxMasterGain);
        this.worldSfxGain.connect(this.sfxMasterGain);
        this.ambienceGain.connect(this.sfxMasterGain);

        // Route SFX directly to the AudioContext destination so they are not scaled by music masterGain.
        this.sfxMasterGain.connect(ac.destination);

        this.sfx = new SfxManager(ac);
        this._updateSfxGainFromState();
    }

    _updateSfxGainFromState() {
        if (!this.sfxMasterGain || !this.audioContext) return;
        const effective = this.sfxMuted ? 0 : this.sfxVolume;
        try {
            const t = this.audioContext.currentTime;
            this.sfxMasterGain.gain.cancelScheduledValues(t);
            this.sfxMasterGain.gain.setTargetAtTime(effective, t, 0.05);
        } catch {
            this.sfxMasterGain.gain.value = effective;
        }
    }

    _ensureSfxReady() {
        if (!this.musicGenerator || !this.musicGenerator.audioContext || !this.musicGenerator.masterGain) return false;
        if (!this.sfxMasterGain || !this.sfx || !this.audioContext) {
            this._initSfxBuses();
        }
        return !!(this.sfx && this.sfxMasterGain && this.uiBusGain);
    }

    _pickRandom(list) {
        if (!Array.isArray(list) || !list.length) return null;
        const i = (Math.random() * list.length) | 0;
        return list[i];
    }

    async _playUiCategory(categoryId, opts = {}) {
        if (!this._ensureSfxReady()) return;
        if (this.sfxMuted || this.sfxVolume <= 0) return;

        // Use base-relative paths so this works regardless of Vite base config.
        // Files live under public/audio/sfx/, which is served as audio/sfx/ at runtime.
        const uiMap = {
            'ui.click':    [ 'audio/sfx/ui/ui_click_01.wav' ],
            'ui.confirm':  [ 'audio/sfx/ui/ui_confirm_01.wav' ],
            'ui.cancel':   [ 'audio/sfx/ui/ui_cancel_01.wav' ],
            'ui.open':     [ 'audio/sfx/ui/ui_open_01.wav' ],
            'ui.close':    [ 'audio/sfx/ui/ui_close_01.wav' ],
            'ui.fanfare':  [ 'audio/sfx/ui/ui_fanfare_01.wav' ],
            'ui.dialog':   [ 'audio/sfx/ui/ui_dialog_01.wav' ],

            // Player actions (non-positional, UI-routed)
            'ui.player.drink': [
                'audio/sfx/player/player_drinking_01.wav',
                'audio/sfx/player/player_drinking_02.wav'
            ]
        };

        const gainMap = {
            'ui.click':    0.5,
            'ui.confirm':  0.6,
            'ui.cancel':   0.5,
            'ui.open':     0.55,
            'ui.close':    0.5,
            'ui.fanfare':  0.7,
            'ui.dialog':   0.35,
            'ui.player.drink': 0.75
        };

        const urls = uiMap[categoryId];
        const url = urls ? this._pickRandom(urls) : null;
        if (!url) return;

        const baseGain = typeof opts.baseGain === 'number'
            ? opts.baseGain
            : (gainMap[categoryId] != null ? gainMap[categoryId] : 0.5);

        // Overall loudness is controlled by the SFX master bus (sfxMasterGain.gain),
        // which is driven by this.sfxVolume. Avoid multiplying by sfxVolume here to
        // keep the slider perceptually linear instead of squaring it.
        const overallGain = baseGain;

        await this.sfx.playOneShot({
            url,
            destination: this.uiBusGain,
            gain: overallGain,
            playbackRate: 1.0,
            playbackRateJitter: 0.015,
            attack: 0.004,
            release: 0.05,
            maxDuration: 2.5
        });
    }

    /**
     * Internal 3D world SFX helper.
     * Plays a positional one-shot routed into worldSfxGain with a PannerNode.
     *
     * @param {string} categoryId - Logical world category id (e.g. "world.impact.melee").
     * @param {number[]} worldPos - [x,y,z] position in world space.
     * @param {Object} [opts]
     */
    async _playWorldCategory(categoryId, worldPos, opts = {}) {
        if (!this._ensureSfxReady()) return;
        if (this.sfxMuted || this.sfxVolume <= 0) return;
        if (!worldPos || worldPos.length < 3) return;
        if (!this.audioContext || !this.worldSfxGain || !this.sfx) return;

        // World SFX palette (positional). Uses the same base-relative paths as UI.
        const worldMap = {
            // Generic melee impact
            'world.impact.melee': [
                'audio/sfx/combat/melee_impact_01.wav'
            ],

            // --- NPC vocal banks (1 file each, pitch jitter provides variation) ---

            // Hellrat (small creature)
            'world.npc.hellrat.attack': [ 'audio/sfx/npc/hellrat/hellrat_attack_01.wav' ],
            'world.npc.hellrat.pain':   [ 'audio/sfx/npc/hellrat/hellrat_pain_01.wav' ],
            'world.npc.hellrat.death':  [ 'audio/sfx/npc/hellrat/hellrat_death_01.wav' ],

            // Rogue (humanoid)
            'world.npc.rogue.attack': [ 'audio/sfx/npc/rogue/rogue_attack_01.wav' ],
            'world.npc.rogue.pain':   [ 'audio/sfx/npc/rogue/rogue_pain_01.wav' ],
            'world.npc.rogue.death':  [ 'audio/sfx/npc/rogue/rogue_death_01.wav' ],

            // Bear (boss)
            'world.npc.bear.attack': [ 'audio/sfx/npc/bear/bear_attack_01.wav' ],
            'world.npc.bear.pain':   [ 'audio/sfx/npc/bear/bear_pain_01.wav' ],
            'world.npc.bear.death':  [ 'audio/sfx/npc/bear/bear_death_01.wav' ],

            // Player-specific death vocal
            'world.player.death': [
                'audio/sfx/player/player_death_01.wav'
            ],

            // Fireball projectile sounds
            'world.combat.fireball.fly':       [ 'audio/sfx/combat/fireball_fly_01.wav' ],
            'world.combat.fireball.explosion':  [ 'audio/sfx/combat/fireball_explosion_01.wav' ],

            // Item pickup sounds (per item type)
            'world.pickup.potion':  [ 'audio/sfx/pickup/pickup_potion_01.wav' ],
            'world.pickup.weapon':  [ 'audio/sfx/pickup/pickup_weapon_01.wav' ],
            'world.pickup.armor':   [ 'audio/sfx/pickup/pickup_armor_01.wav' ],
            'world.pickup.key':     [ 'audio/sfx/pickup/pickup_key_01.wav' ],
            'world.pickup.quest':   [ 'audio/sfx/pickup/pickup_quest_01.wav' ],

            // Player combat sounds (generated synthesized effects)
            'world.combat.punch': [
                'audio/sfx/combat/punch_01.wav',
                'audio/sfx/combat/punch_02.wav',
                'audio/sfx/combat/punch_03.wav'
            ],
            'world.combat.kick': [
                'audio/sfx/combat/kick_01.wav',
                'audio/sfx/combat/kick_02.wav',
                'audio/sfx/combat/kick_03.wav'
            ],
            'world.combat.fireball': [
                'audio/sfx/combat/fireball_01.wav',
                'audio/sfx/combat/fireball_02.wav',
                'audio/sfx/combat/fireball_03.wav'
            ],

            // Jump / landing
            'world.movement.jump': [
                'audio/sfx/movement/jump_01.wav',
                'audio/sfx/movement/jump_02.wav',
                'audio/sfx/movement/jump_03.wav'
            ],
            'world.movement.landing': [
                'audio/sfx/movement/landing_01.wav',
                'audio/sfx/movement/landing_02.wav',
                'audio/sfx/movement/landing_03.wav'
            ],

            // Fires & torches
            // Torch always uses a single, consistent ignite/extinguish timbre.
            'world.fire.torch.ignite': [
                'audio/sfx/fires/fire_ignite_small_01.wav'
            ],
            'world.fire.torch.extinguish': [
                'audio/sfx/fires/fire_extinguish_water_01.wav'
            ],

            // Water movement / impacts
            'world.water.splash': [
                'audio/sfx/movement/water_splash_01.wav',
                'audio/sfx/movement/water_splash_02.wav',
                'audio/sfx/movement/water_splash_03.wav'
            ],

            // Footsteps – per NPC type, walk and run variants
            'world.footstep.humanoid.walk': [ 'audio/sfx/footsteps/footstep_humanoid_walk_01.wav' ],
            'world.footstep.humanoid.run':  [ 'audio/sfx/footsteps/footstep_humanoid_run_01.wav' ],
            'world.footstep.bear.walk':     [ 'audio/sfx/footsteps/footstep_bear_walk_01.wav' ],
            'world.footstep.bear.run':      [ 'audio/sfx/footsteps/footstep_bear_run_01.wav' ],
            'world.footstep.hellrat.walk':  [ 'audio/sfx/footsteps/footstep_hellrat_walk_01.wav' ],
            'world.footstep.hellrat.run':   [ 'audio/sfx/footsteps/footstep_hellrat_run_01.wav' ]
        };

        const gainMap = {
            'world.impact.melee': 0.9,

            // NPC vocals
            'world.npc.hellrat.attack': 0.8,
            'world.npc.hellrat.pain': 0.8,
            'world.npc.hellrat.death': 0.85,

            'world.npc.rogue.attack': 0.75,
            'world.npc.rogue.pain': 0.8,
            'world.npc.rogue.death': 0.9,

            'world.npc.bear.attack': 0.95,
            'world.npc.bear.pain': 0.95,
            'world.npc.bear.death': 1.0,

            'world.player.death': 0.95,

            // Player combat
            'world.combat.punch': 0.9,
            'world.combat.kick': 0.95,
            'world.combat.fireball': 0.85,
            // fireball.fly gain is managed directly in playFireballFly() (0.35 base)
            'world.combat.fireball.explosion': 0.9,

            // Item pickup
            'world.pickup.potion': 0.8,
            'world.pickup.weapon': 0.8,
            'world.pickup.armor':  0.8,
            'world.pickup.key':    0.85,
            'world.pickup.quest':  0.9,

            // Jump / landing
            'world.movement.jump': 0.7,
            'world.movement.landing': 0.85,

            // Fires & torches
            'world.fire.torch.ignite': 0.85,
            'world.fire.torch.extinguish': 0.9,

            // Water movement
            'world.water.splash': 1.0,
 
            // Footsteps – per NPC type (run louder than walk)
            'world.footstep.humanoid.walk': 1.3,
            'world.footstep.humanoid.run':  1.6,
            'world.footstep.bear.walk':     1.5,
            'world.footstep.bear.run':      1.8,
            'world.footstep.hellrat.walk':  1.1,
            'world.footstep.hellrat.run':   1.3
        };

        const distanceMap = {
            // Melee impact
            'world.impact.melee': { refDistance: 2.5, maxDistance: 32, rolloffFactor: 1.15 },

            // NPC vocals
            'world.npc.hellrat.attack': { refDistance: 3.0, maxDistance: 36, rolloffFactor: 1.0 },
            'world.npc.hellrat.pain':   { refDistance: 3.0, maxDistance: 34, rolloffFactor: 1.0 },
            'world.npc.hellrat.death':  { refDistance: 3.5, maxDistance: 40, rolloffFactor: 0.95 },

            'world.npc.rogue.attack': { refDistance: 3.0, maxDistance: 40, rolloffFactor: 1.0 },
            'world.npc.rogue.pain':   { refDistance: 3.0, maxDistance: 40, rolloffFactor: 1.0 },
            'world.npc.rogue.death':  { refDistance: 3.5, maxDistance: 45, rolloffFactor: 0.95 },

            'world.npc.bear.attack': { refDistance: 3.5, maxDistance: 48, rolloffFactor: 0.95 },
            'world.npc.bear.pain':   { refDistance: 3.5, maxDistance: 46, rolloffFactor: 0.95 },
            'world.npc.bear.death':  { refDistance: 4.0, maxDistance: 52, rolloffFactor: 0.9 },

            'world.player.death': { refDistance: 3.5, maxDistance: 48, rolloffFactor: 0.9 },

            // Player combat
            'world.combat.punch': { refDistance: 2.5, maxDistance: 35, rolloffFactor: 1.0 },
            'world.combat.kick': { refDistance: 3.0, maxDistance: 40, rolloffFactor: 1.0 },
            'world.combat.fireball': { refDistance: 3.5, maxDistance: 50, rolloffFactor: 0.9 },
            // fireball.fly distance is managed directly in playFireballFly()
            'world.combat.fireball.explosion':  { refDistance: 4.0, maxDistance: 50, rolloffFactor: 0.85 },

            // Fires & torches – localized but with a bit of reach for ambience
            'world.fire.torch.ignite': { refDistance: 2.0, maxDistance: 18, rolloffFactor: 1.1 },
            'world.fire.torch.extinguish': { refDistance: 2.2, maxDistance: 20, rolloffFactor: 1.1 },

            // Water impacts (splash) – similar reach to small melee impacts
            'world.water.splash': { refDistance: 3.0, maxDistance: 32, rolloffFactor: 1.15 },
 
            // Footsteps – per NPC type (run carries farther)
            'world.footstep.humanoid.walk': { refDistance: 5.0, maxDistance: 200, rolloffFactor: 0.8 },
            'world.footstep.humanoid.run':  { refDistance: 6.0, maxDistance: 200, rolloffFactor: 0.8 },
            'world.footstep.bear.walk':     { refDistance: 6.0, maxDistance: 200, rolloffFactor: 0.8 },
            'world.footstep.bear.run':      { refDistance: 7.5, maxDistance: 200, rolloffFactor: 0.8 },
            'world.footstep.hellrat.walk':  { refDistance: 4.0, maxDistance: 200, rolloffFactor: 0.8 },
            'world.footstep.hellrat.run':   { refDistance: 5.0, maxDistance: 200, rolloffFactor: 0.8 }
        };

        // Per-group mix multipliers (gain + distance) driven by SettingsMenu.
        const mix = this.mix || null;
        let gainMul = 1.0;
        let distanceMul = 1.0;
        let mixGroup = opts.mixGroup || null;
        if (mix) {
            if (!mixGroup) {
                if (categoryId.startsWith('world.footstep.')) {
                    // Default footsteps to NPC group; player footsteps pass an explicit group.
                    mixGroup = 'npcFootstep';
                } else if (categoryId.startsWith('world.weapon.') || categoryId.startsWith('world.impact.')) {
                    mixGroup = 'combat';
                } else if (categoryId.startsWith('world.npc.')) {
                    mixGroup = 'npcVoice';
                } else if (categoryId.startsWith('world.fire.')) {
                    mixGroup = 'fire';
                } else if (categoryId.startsWith('world.water.')) {
                    // Water movement SFX share the footsteps sliders (volume/range).
                    mixGroup = 'npcFootstep';
                }
            }
 
            switch (mixGroup) {
                case 'playerFootstep':
                    gainMul = typeof mix.playerFootstepGain === 'number' ? mix.playerFootstepGain : 1.0;
                    distanceMul = typeof mix.footstepDistanceMul === 'number' ? mix.footstepDistanceMul : 1.0;
                    break;
                case 'npcFootstep':
                    gainMul = typeof mix.npcFootstepGain === 'number' ? mix.npcFootstepGain : 1.0;
                    distanceMul = typeof mix.footstepDistanceMul === 'number' ? mix.footstepDistanceMul : 1.0;
                    break;
                case 'combat':
                    gainMul = typeof mix.combatGain === 'number' ? mix.combatGain : 1.0;
                    distanceMul = typeof mix.combatDistanceMul === 'number' ? mix.combatDistanceMul : 1.0;
                    break;
                case 'npcVoice':
                    gainMul = typeof mix.npcVoiceGain === 'number' ? mix.npcVoiceGain : 1.0;
                    distanceMul = typeof mix.npcVoiceDistanceMul === 'number' ? mix.npcVoiceDistanceMul : 1.0;
                    break;
                case 'fire':
                    gainMul = typeof mix.fireGain === 'number' ? mix.fireGain : 1.0;
                    distanceMul = typeof mix.fireDistanceMul === 'number' ? mix.fireDistanceMul : 1.0;
                    break;
                default:
                    break;
            }
        }
 
        // Extra manual attenuation + hard cutoff based on listener/emitter distance,
        // to guarantee that emitters are truly silent beyond their designed range
        // even if the browser's PannerNode behavior changes.
        // Player footsteps are treated as "on the listener" and are not distance-attenuated.
        let distanceScalar = 1.0;
        const skipManualDistance = (mixGroup === 'playerFootstep');
        try {
            if (!skipManualDistance &&
                this.listenerPos && this.listenerPos.length >= 3 &&
                Array.isArray(worldPos) && worldPos.length >= 3) {
                const lp = this.listenerPos;
                const dx = Number(worldPos[0]) - lp[0];
                const dy = Number(worldPos[1]) - lp[1];
                const dz = Number(worldPos[2]) - lp[2];
                const d = Math.hypot(dx, dy, dz);
 
                const distCfg = distanceMap[categoryId] || {};
                const baseMax = (typeof distCfg.maxDistance === 'number' ? distCfg.maxDistance : 40.0);
                const mul = (typeof distanceMul === 'number' && distanceMul > 0) ? distanceMul : 1.0;
                const range = baseMax * mul;
 
 
                if (range > 0) {
                    if (d >= range) {
                        // Hard cut: do not even schedule this sound when beyond range.
                        return;
                    }
                    // Simple linear fade from 1.0 at d=0 to 0.0 at d=range.
                    distanceScalar = 1.0 - (d / range);
                }
            }
        } catch {}
 
        const urls = worldMap[categoryId];
        if (!urls || !urls.length) return;

        // Choose a specific variant when requested, otherwise fall back to light randomization.
        let url = null;
        if (typeof opts.variantIndex === 'number' && urls.length > 0) {
            let idx = opts.variantIndex | 0;
            if (idx < 0) idx = -idx;
            idx = idx % urls.length;
            url = urls[idx];
        } else if (opts.variantSeed !== undefined && opts.variantSeed !== null && urls.length > 0) {
            // Deterministic variant selection based on a seed (e.g. characterId+category+sideSign)
            // so each character/boot pair uses the same subset of samples on a given surface.
            let seed = 0;
            if (typeof opts.variantSeed === 'number' && Number.isFinite(opts.variantSeed)) {
                seed = opts.variantSeed | 0;
            } else if (typeof opts.variantSeed === 'string') {
                // Simple string hash; stable across sessions
                for (let i = 0; i < opts.variantSeed.length; i++) {
                    seed = ((seed << 5) - seed) + opts.variantSeed.charCodeAt(i);
                    seed |= 0;
                }
            }
            if (seed < 0) seed = -seed;
            const idx = urls.length > 0 ? (seed % urls.length) : 0;
            url = urls[idx];
        } else {
            url = this._pickRandom(urls);
        }
        if (!url) return;

        const baseGain = (typeof opts.baseGain === 'number')
            ? opts.baseGain
            : (gainMap[categoryId] != null ? gainMap[categoryId] : 0.9);
        const extraMul = (typeof opts.gainMultiplier === 'number') ? opts.gainMultiplier : 1.0;
        let overallGain = baseGain * gainMul * distanceScalar * extraMul;
        if (overallGain <= 0) return;
 
        const ac = this.audioContext;
        const buffer = await this.sfx.loadBuffer(url);
        if (!buffer || !ac) return;

        let source = null;
        let envelope = null;
        let panner = null;
        try {
            source = ac.createBufferSource();
            envelope = ac.createGain();
            panner = ac.createPanner();

            source.buffer = buffer;

            let rate = typeof opts.playbackRate === 'number' ? opts.playbackRate : 1.0;
            const jitter = typeof opts.playbackRateJitter === 'number' ? opts.playbackRateJitter : 0.02;
            if (jitter > 0) {
                const r = (Math.random() * 2 - 1) * jitter;
                rate *= (1 + r);
            }
            source.playbackRate.value = rate;

            // Configure distance/panning
            panner.panningModel = 'HRTF';
            panner.distanceModel = 'inverse';

            const distCfg = distanceMap[categoryId] || {};
            const refBase = (typeof distCfg.refDistance === 'number' ? distCfg.refDistance : 3.0);
            const maxBase = (typeof distCfg.maxDistance === 'number' ? distCfg.maxDistance : 40.0);
            const rollBase = (typeof distCfg.rolloffFactor === 'number' ? distCfg.rolloffFactor : 1.0);
 
            let refOverride, maxOverride, rollOverride;
 
            if (mixGroup === 'playerFootstep') {
                // Player footsteps are treated as "on the listener": we keep full strength
                // regardless of world distance and only use panning for subtle stereo.
                refOverride = 1.0;
                maxOverride = 1e6;
                rollOverride = 0.0;
            } else {
                // Per-group distance multipliers strictly control how far the sound carries
                // without changing its loudness when the listener is close. refDistance
                // stays anchored at the authored value unless explicitly overridden.
                refOverride = typeof opts.refDistance === 'number'
                    ? opts.refDistance
                    : refBase;
                maxOverride = typeof opts.maxDistance === 'number'
                    ? opts.maxDistance
                    : (maxBase * distanceMul);
                rollOverride = typeof opts.rolloffFactor === 'number'
                    ? opts.rolloffFactor
                    : rollBase;
            }
 
            panner.refDistance = refOverride;
            panner.maxDistance = maxOverride;
            panner.rolloffFactor = rollOverride;

            // We treat most of these as omni sources; cones can be added later per-emitter.
            panner.coneInnerAngle = 360;
            panner.coneOuterAngle = 0;
            panner.coneOuterGain = 0;

            const [px, py, pz] = this._screenAlignedPos(
                Number(worldPos[0]) || 0,
                Number(worldPos[1]) || 0,
                Number(worldPos[2]) || 0
            );
            if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
                const t = ac.currentTime;
                panner.positionX.setValueAtTime(px, t);
                panner.positionY.setValueAtTime(py, t);
                panner.positionZ.setValueAtTime(pz, t);
            } else if (typeof panner.setPosition === 'function') {
                panner.setPosition(px, py, pz);
            }

            const now = ac.currentTime;
            const startTime = now + 0.002;
            const attack = Math.max(0.001, typeof opts.attack === 'number' ? opts.attack : 0.004);
            const release = Math.max(0.01, typeof opts.release === 'number' ? opts.release : 0.08);

            envelope.gain.setValueAtTime(0, startTime);
            envelope.gain.linearRampToValueAtTime(overallGain, startTime + attack);

            let naturalDuration = buffer.duration;
            if (typeof opts.maxDuration === 'number' && opts.maxDuration > 0) {
                naturalDuration = Math.min(naturalDuration, opts.maxDuration);
            }
            const endTime = startTime + naturalDuration;
            const releaseStart = Math.max(startTime + attack * 0.5, endTime - release);

            envelope.gain.setValueAtTime(overallGain, releaseStart);
            envelope.gain.linearRampToValueAtTime(0.0001, releaseStart + release);

            source.connect(envelope);
            envelope.connect(panner);
            panner.connect(this.worldSfxGain);

            source.start(startTime);
            source.stop(releaseStart + release + 0.05);

            // Explicitly disconnect nodes after playback to help GC.
            // Without this, the browser may retain audio graph references
            // indefinitely, causing OOM over long sessions (8+ hours).
            source.onended = () => {
                source.disconnect();
                envelope.disconnect();
                panner.disconnect();
                source.onended = null;
            };
        } catch (e) {
            try { Debug.warn('Audio', 'World SFX failed for category', categoryId, e); } catch {}
            try {
                if (source) { source.disconnect(); source.stop(); }
                if (envelope) envelope.disconnect();
                if (panner) panner.disconnect();
            } catch {}
        }
    }

    _resolveNpcVoiceBank(npcType) {
        const t = (npcType || '').toLowerCase();
        if (t.includes('hellrat')) return 'hellrat';
        if (t.includes('bear')) return 'bear';
        // Default humanoid bank — used by rogue and any unrecognised type.
        return 'rogue';
    }

    /**
     * Resolve a weapon swing category from weaponType string.
     * Maps to punch (default), kick, or fireball cast sound.
     */
    _resolveWeaponSwingCategory(meta = {}) {
        let w = meta.weaponType;

        if (typeof w === 'string') {
            const s = w.toLowerCase();
            if (s === 'kick') return 'world.combat.kick';
            if (s === 'fireball' || s === 'fire' || s === 'spell') return 'world.combat.fireball';
            if (s === 'blunt' || s === 'mace' || s === 'club') return 'world.combat.kick';
        }

        // Default: punch (covers punch, unarmed, sword, dagger, claw, etc.)
        return 'world.combat.punch';
    }

    /**
     * Resolve footstep category from NPC type + walk/run.
     */
    _resolveFootstepCategory(meta = {}) {
        const npcType = (meta.npcType || '').toLowerCase();
        const gait = meta.isHeavy ? 'run' : 'walk';
        let base = 'humanoid';
        if (npcType.includes('bear')) base = 'bear';
        else if (npcType.includes('hellrat')) base = 'hellrat';
        return `world.footstep.${base}.${gait}`;
    }

    /**
     * Play any world category sound non-spatially (always centered, no panning).
     * Routes through _playWorldCategory at the listener position with zero
     * rolloff so the PannerNode is effectively transparent.
     * Used for player-originated combat sounds etc.
     * @private
     */
    async _playNonSpatialCategory(categoryId, opts = {}) {
        const listenerPos = this.listenerPos || [0, 0, 0];
        return this._playWorldCategory(categoryId, listenerPos, {
            ...opts,
            mixGroup: opts.mixGroup || 'combat',
            refDistance: 1.0,
            maxDistance: 1e6,
            rolloffFactor: 0.0
        });
    }

    /**
     * Play a jump sound. Player jumps are non-spatial; NPC jumps are positional.
     */
    playJump(meta = {}) {
        if (!meta) return;
        const sourceKind = (meta.source || '').toLowerCase();
        if (sourceKind === 'player') {
            this._playNonSpatialCategory('world.movement.jump', {
                playbackRateJitter: 0.06,
                maxDuration: 0.25,
                attack: 0.002,
                release: 0.03
            }).catch(() => {});
        } else {
            const worldPos = this._toVec3(meta.worldPos);
            if (!worldPos) return;
            this._playWorldCategory('world.movement.jump', worldPos, {
                playbackRateJitter: 0.06,
                maxDuration: 0.25,
                attack: 0.002,
                release: 0.03
            }).catch(() => {});
        }
    }

    /**
     * Play a landing sound. Player landings are non-spatial; NPC landings are positional.
     */
    playLanding(meta = {}) {
        if (!meta) return;
        const dropHeight = meta.dropHeight || 0;

        // Skip very small drops (less than half a cuboid)
        if (dropHeight < 0.5) return;

        // Scale intensity by drop height (quadratic for gentle low-end):
        //   1 unit  → very subtle (~6%)
        //   5+ units → full intensity (1.0)
        const t = Math.min(1, Math.max(0, (dropHeight - 0.5) / 4.5));
        const intensity = 0.03 + 0.97 * (t * t);

        // Lower pitch for heavier drops (1.0 → 0.85)
        const pitchShift = 1.0 - 0.15 * t;

        const sourceKind = (meta.source || '').toLowerCase();
        if (sourceKind === 'player') {
            this._playNonSpatialCategory('world.movement.landing', {
                playbackRateJitter: 0.05,
                playbackRate: pitchShift,
                gainMultiplier: intensity,
                maxDuration: 0.3,
                attack: 0.002,
                release: 0.04
            }).catch(() => {});
        } else {
            const worldPos = this._toVec3(meta.worldPos);
            if (!worldPos) return;
            this._playWorldCategory('world.movement.landing', worldPos, {
                playbackRateJitter: 0.05,
                playbackRate: pitchShift,
                gainMultiplier: intensity,
                maxDuration: 0.3,
                attack: 0.002,
                release: 0.04
            }).catch(() => {});
        }
    }

    /**
     * Play a footstep sound non-spatially (always centered, no panning).
     * Used for player footsteps to provide a first-person audio perspective.
     * @private
     */
    async _playNonSpatialFootstep(categoryId, opts = {}) {
        if (!this._ensureSfxReady()) return;
        if (this.sfxMuted || this.sfxVolume <= 0) return;
        if (!this.audioContext || !this.worldSfxGain || !this.sfx) return;

        // Footstep palette – mirrors the worldMap in _playWorldCategory
        const worldMap = {
            'world.footstep.humanoid.walk': [ 'audio/sfx/footsteps/footstep_humanoid_walk_01.wav' ],
            'world.footstep.humanoid.run':  [ 'audio/sfx/footsteps/footstep_humanoid_run_01.wav' ],
            'world.footstep.bear.walk':     [ 'audio/sfx/footsteps/footstep_bear_walk_01.wav' ],
            'world.footstep.bear.run':      [ 'audio/sfx/footsteps/footstep_bear_run_01.wav' ],
            'world.footstep.hellrat.walk':  [ 'audio/sfx/footsteps/footstep_hellrat_walk_01.wav' ],
            'world.footstep.hellrat.run':   [ 'audio/sfx/footsteps/footstep_hellrat_run_01.wav' ]
        };

        const urls = worldMap[categoryId];
        if (!urls || !urls.length) return;

        // Pick variant using the same logic as _playWorldCategory
        let url = null;
        if (opts.variantSeed !== undefined && opts.variantSeed !== null) {
            let seed = 0;
            if (typeof opts.variantSeed === 'number' && Number.isFinite(opts.variantSeed)) {
                seed = opts.variantSeed | 0;
            } else if (typeof opts.variantSeed === 'string') {
                for (let i = 0; i < opts.variantSeed.length; i++) {
                    seed = ((seed << 5) - seed) + opts.variantSeed.charCodeAt(i);
                    seed |= 0;
                }
            }
            if (seed < 0) seed = -seed;
            const idx = urls.length > 0 ? (seed % urls.length) : 0;
            url = urls[idx];
        } else {
            url = this._pickRandom(urls);
        }
        if (!url) return;

        // Apply playerFootstepGain from mix
        const gainMap = {
            'world.footstep.humanoid.walk': 1.3,
            'world.footstep.humanoid.run':  1.6,
            'world.footstep.bear.walk':     1.5,
            'world.footstep.bear.run':      1.8,
            'world.footstep.hellrat.walk':  1.1,
            'world.footstep.hellrat.run':   1.3
        };

        const baseGain = gainMap[categoryId] != null ? gainMap[categoryId] : 1.0;
        const playerGain = (this.mix && typeof this.mix.playerFootstepGain === 'number')
            ? this.mix.playerFootstepGain
            : 1.0;
        const extraMul = (typeof opts.gainMultiplier === 'number') ? opts.gainMultiplier : 1.0;
        const overallGain = baseGain * playerGain * extraMul;

        if (overallGain <= 0) return;

        // Play through worldSfxGain without spatial panning
        await this.sfx.playOneShot({
            url: url,
            destination: this.worldSfxGain,
            gain: overallGain,
            playbackRate: typeof opts.playbackRate === 'number' ? opts.playbackRate : 1.0,
            playbackRateJitter: opts.playbackRateJitter || 0.015,
            attack: opts.attack || 0.003,
            release: opts.release || 0.06,
            maxDuration: opts.maxDuration || 0.9
        });
    }

    /**
     * Positional footstep SFX at a world position.
     * Used by the main game loop to render player + NPC footsteps.
     *
     * meta: {
     *   worldPos: [x,y,z],
     *   source?: 'player' | 'npc' | string,
     *   npcType?: string,
     *   isHeavy?: boolean,
     *   inWater?: boolean
     * }
     */
    playWorldFootstep(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;
        const categoryId = this._resolveFootstepCategory(meta);
        if (!categoryId) return;

        // Derive a stable per-character, per-surface, per-foot variant seed so that
        // each character effectively has a consistent "pair of boots" on a given
        // surface (e.g. left/right variants that do not randomize every step).
        let variantSeed = undefined;
        try {
            const cid = (meta.characterId != null)
                ? String(meta.characterId)
                : String(meta.source || meta.sourceKind || '');
            const sideCode =
                (typeof meta.sideSign === 'number' && meta.sideSign !== 0)
                    ? (meta.sideSign < 0 ? 'L' : 'R')
                    : 'X';
            variantSeed = `${cid}|${categoryId}|${sideCode}`;
        } catch {
            variantSeed = undefined;
        }

        const sourceKind = (meta.source || meta.sourceKind || '').toLowerCase();

        // Random gain variation (±15%) for natural feel
        const gainVariation = 0.85 + Math.random() * 0.3;

        if (sourceKind === 'player') {
            // Player footsteps: non-spatial (always centered, first-person perspective)
            this._playNonSpatialFootstep(categoryId, {
                playbackRateJitter: 0.06,
                gainMultiplier: gainVariation,
                maxDuration: 0.35,
                attack: 0.003,
                release: 0.04,
                variantSeed
            }).catch(() => {});
            return;
        }

        // NPC/creature footsteps: positional
        this._playWorldCategory(categoryId, worldPos, {
            playbackRateJitter: 0.06,
            gainMultiplier: gainVariation,
            maxDuration: 0.35,
            attack: 0.003,
            release: 0.04,
            mixGroup: 'npcFootstep',
            variantSeed
        }).catch(() => {});
    }

    /**
     * Positional weapon swing SFX (pre-impact whoosh) at a world position.
     * Maps to punch (default), kick, or fireball cast sound.
     */
    playWorldWeaponSwing(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;
        const categoryId = this._resolveWeaponSwingCategory(meta);
        if (!categoryId) return;

        const sourceKind = (meta.source || '').toLowerCase();
        if (sourceKind === 'player') {
            // Player attacks: non-spatial (always centered)
            this._playNonSpatialCategory(categoryId, {
                playbackRateJitter: 0.04,
                maxDuration: 0.6,
                attack: 0.003,
                release: 0.05
            }).catch(() => {});
        } else {
            this._playWorldCategory(categoryId, worldPos, {
                playbackRateJitter: 0.02,
                maxDuration: 1.2,
                attack: 0.003,
                release: 0.06,
                mixGroup: 'combat'
            }).catch(() => {});
        }
    }

    /**
     * Positional melee impact at a world position.
     */
    playWorldMeleeImpact(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;

        const categoryId = 'world.impact.melee';

        // Slightly stronger for lethal / crit hits.
        let baseGainMul = 1.0;
        if (meta.isLethal) baseGainMul *= 1.2;
        if (meta.isCrit) baseGainMul *= 1.1;

        const sourceKind = (meta.source || '').toLowerCase();
        if (sourceKind === 'player') {
            // Player impacts: non-spatial (always centered)
            this._playNonSpatialCategory(categoryId, {
                playbackRateJitter: 0.04,
                maxDuration: 0.5,
                attack: 0.003,
                release: 0.05,
                gainMultiplier: baseGainMul
            }).catch(() => {});
        } else {
            this._playWorldCategory(categoryId, worldPos, {
                baseGain: undefined,
                playbackRateJitter: 0.02,
                maxDuration: 1.5,
                attack: 0.003,
                release: 0.07,
                mixGroup: 'combat',
                gainMultiplier: baseGainMul
            }).catch(() => {});
        }
    }

    /**
     * NPC attack grunt/bark when swinging at the player.
     */
    playWorldNpcAttackVocal(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;
        const bank = this._resolveNpcVoiceBank(meta.npcType);
        const cat = `world.npc.${bank}.attack`;

        this._playWorldCategory(cat, worldPos, {
            playbackRateJitter: 0.02,
            maxDuration: 2.5,
            attack: 0.004,
            release: 0.12,
            mixGroup: 'npcVoice'
        }).catch(() => {});
    }

    /**
     * NPC pain reaction (non-lethal hit).
     */
    playWorldNpcPain(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;
        const bank = this._resolveNpcVoiceBank(meta.npcType);
        const cat = `world.npc.${bank}.pain`;

        // Heavier hits push gain slightly.
        const dmg = Number(meta.damage) || 0;
        const maxHealth = Number(meta.maxHealth) || 1;
        const frac = maxHealth > 0 ? (dmg / maxHealth) : 0;
        const heavy = !!meta.isHeavy || frac >= 0.35;

        const baseGain = heavy ? 0.95 : 0.8;

        this._playWorldCategory(cat, worldPos, {
            baseGain,
            playbackRateJitter: 0.02,
            maxDuration: 2.6,
            attack: 0.005,
            release: 0.15,
            mixGroup: 'npcVoice'
        }).catch(() => {});
    }

    /**
     * NPC death vocal.
     */
    playWorldNpcDeath(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;
        const bank = this._resolveNpcVoiceBank(meta.npcType);
        const cat = `world.npc.${bank}.death`;

        this._playWorldCategory(cat, worldPos, {
            playbackRateJitter: 0.015,
            maxDuration: 3.0,
            attack: 0.004,
            release: 0.2,
            mixGroup: 'npcVoice'
        }).catch(() => {});
    }

    /**
     * Player death vocal (You Died scream).
     *
     * Uses a dedicated player death palette so the hero has a distinct
     * vocal separate from generic humanoid NPCs.
     */
    playWorldPlayerDeath(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;

        const cat = 'world.player.death';

        this._playWorldCategory(cat, worldPos, {
            playbackRateJitter: 0.01,
            maxDuration: 3.0,
            attack: 0.004,
            release: 0.22,
            mixGroup: 'npcVoice'
        }).catch(() => {});
    }

    /**
     * Fireball in-flight whoosh. Returns a handle so the caller can fade
     * the volume in sync with the visual sprite fade-out.
     *
     * @returns {Promise<{setGain: (v:number)=>void, stop: ()=>void}|null>}
     */
    async playFireballFly(meta = {}) {
        if (!meta) return null;
        if (!this._ensureSfxReady()) return null;
        if (this.sfxMuted || this.sfxVolume <= 0) return null;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return null;

        const ac = this.audioContext;
        if (!ac || !this.worldSfxGain || !this.sfx) return null;

        const url = 'audio/sfx/combat/fireball_fly_01.wav';
        const buffer = await this.sfx.loadBuffer(url);
        if (!buffer) return null;

        try {
            const source = ac.createBufferSource();
            const envelope = ac.createGain();
            const panner = ac.createPanner();

            source.buffer = buffer;
            source.playbackRate.value = 1.0 + (Math.random() - 0.5) * 0.08;

            panner.panningModel = 'HRTF';
            panner.distanceModel = 'inverse';
            panner.refDistance = 3.0;
            panner.maxDistance = 40;
            panner.rolloffFactor = 0.9;
            panner.setPosition(worldPos[0], worldPos[1], worldPos[2]);

            const baseGain = 0.47;
            const mix = this.mix || null;
            const gainMul = (mix && typeof mix.combatGain === 'number') ? mix.combatGain : 1.0;
            const peakGain = baseGain * gainMul;

            const now = ac.currentTime;
            envelope.gain.setValueAtTime(0, now);
            envelope.gain.linearRampToValueAtTime(peakGain, now + 0.01);

            source.connect(envelope);
            envelope.connect(panner);
            panner.connect(this.worldSfxGain);
            source.start(now);

            // Auto-stop after buffer duration + safety margin
            const maxTime = buffer.duration + 0.5;
            source.stop(now + maxTime);

            let stopped = false;
            return {
                setGain(v) {
                    if (stopped) return;
                    try {
                        const t = ac.currentTime;
                        envelope.gain.cancelScheduledValues(t);
                        envelope.gain.setValueAtTime(
                            Math.max(0, v) * peakGain, t
                        );
                    } catch {}
                },
                stop() {
                    if (stopped) return;
                    stopped = true;
                    try {
                        const t = ac.currentTime;
                        envelope.gain.cancelScheduledValues(t);
                        envelope.gain.linearRampToValueAtTime(0, t + 0.05);
                        source.stop(t + 0.1);
                    } catch {}
                }
            };
        } catch {
            return null;
        }
    }

    /**
     * Fireball explosion on impact.
     */
    playFireballExplosion(meta = {}) {
        if (!meta) return;
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;
        this._playWorldCategory('world.combat.fireball.explosion', worldPos, {
            playbackRateJitter: 0.03,
            maxDuration: 1.0,
            attack: 0.003,
            release: 0.1,
            mixGroup: 'combat'
        }).catch(() => {});
    }

    /**
     * Non-spatial item pickup sound, mapped by ITEM_TYPES category.
     * @param {string} itemType - 'consumable'|'weapon'|'armor'|'key'|'quest'
     */
    playPickupSound(itemType) {
        const map = {
            consumable: 'world.pickup.potion',
            weapon:     'world.pickup.weapon',
            armor:      'world.pickup.armor',
            key:        'world.pickup.key',
            quest:      'world.pickup.quest'
        };
        const cat = map[(itemType || '').toLowerCase()] || 'world.pickup.potion';
        this._playNonSpatialCategory(cat, {
            playbackRateJitter: 0.03,
            maxDuration: 0.8,
            attack: 0.003,
            release: 0.06
        }).catch(() => {});
    }

    /**
     * Internal helper to create a looping positional fire source (torch / fireplace).
     *
     * @param {'torch'|'fireplace.brazier'|'fireplace.bonfire'} kind
     * @param {number[]} worldPos
     * @param {AudioNode} busNode
     * @returns {Promise<{source: AudioBufferSourceNode, envelope: GainNode, panner: PannerNode, kind: string}>}
     */
    _createFireLoop(kind, worldPos, busNode) {
        const self = this;
        return (async () => {
            if (!self._ensureSfxReady()) return null;
            if (self.sfxMuted || self.sfxVolume <= 0) return null;
            if (!Array.isArray(worldPos) || worldPos.length < 3) return null;
            if (!self.audioContext || !busNode || !self.sfx) return null;

            const ac = self.audioContext;

            const urlMap = {
                'torch': [
                    'audio/sfx/fires/fire_torch_close_loop_01.wav'
                ],
                'fireplace.brazier': [
                    'audio/sfx/fires/fire_brazier_medium_loop_01.wav'
                ],
                'fireplace.bonfire': [
                    'audio/sfx/fires/fire_bonfire_large_loop_01.wav'
                ]
            };
            const urls = urlMap[kind];
            if (!urls || !urls.length) return null;
            const url = self._pickRandom(urls);
            const buffer = await self.sfx.loadBuffer(url);
            if (!buffer) return null;

            let source = null;
            let envelope = null;
            let panner = null;
            try {
                source = ac.createBufferSource();
                envelope = ac.createGain();

                // Torch is effectively welded to the player; to avoid any distance bugs when
                // moving far from the origin, we treat it as non-positional. World fires
                // (braziers / bonfires) remain fully positional.
                const usePanner = (kind !== 'torch');
                if (usePanner) {
                    panner = ac.createPanner();
                }

                source.buffer = buffer;
                source.loop = true;

                // Gentle playback-rate jitter for variation
                let rate = 1.0;
                const jitter = 0.01;
                const r = (Math.random() * 2 - 1) * jitter;
                rate *= (1 + r);
                source.playbackRate.value = rate;

                let distCfg = null;
                if (usePanner) {
                    panner.panningModel = 'equalpower';
                    panner.distanceModel = 'inverse';
 
                    // Distance falloff per kind. We keep torches welded to the player (non-positional),
                    // but braziers/bonfires are fully positional with a decisive falloff so that by
                    // roughly "one large camp away" they are effectively inaudible.
                    distCfg =
                        kind === 'fireplace.brazier'
                            ? { refDistance: 2.0, maxDistance: 40.0, rolloffFactor: 3.0 }
                            // Bonfires carry a bit further but still die off firmly at distance.
                            : { refDistance: 3.0, maxDistance: 50.0, rolloffFactor: 2.5 };
 
                    const mix = self.mix || null;
                    const distMul = mix && typeof mix.fireDistanceMul === 'number' ? mix.fireDistanceMul : 1.0;
 
                    // Treat fireDistanceMul purely as a max-range control for
                    // fireplace/bonfire ambience. refDistance remains authored so
                    // near-field loudness does not change when adjusting range.
                    panner.refDistance = distCfg.refDistance;
                    panner.maxDistance = distCfg.maxDistance * distMul;
                    panner.rolloffFactor = distCfg.rolloffFactor;

                    const [px, py, pz] = this._screenAlignedPos(
                        Number(worldPos[0]) || 0,
                        Number(worldPos[1]) || 0,
                        Number(worldPos[2]) || 0
                    );
                    if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
                        const t = ac.currentTime;
                        panner.positionX.setValueAtTime(px, t);
                        panner.positionY.setValueAtTime(py, t);
                        panner.positionZ.setValueAtTime(pz, t);
                    } else if (typeof panner.setPosition === 'function') {
                        panner.setPosition(px, py, pz);
                    }
                }

                const now = ac.currentTime;
                const startTime = now + 0.01;
                const attack = 0.12;

                // Relative loudness per fire type. Torch is strong but not overpowering;
                // braziers sit slightly above torch, bonfires are clearly the loudest.
                // Overall loudness is still governed by the global SFX + fire mix sliders.
                let baseGain =
                    kind === 'torch' ? 1.2 :
                    (kind === 'fireplace.brazier' ? 1.4
                                                   : 1.8); // bonfire

                const mixForFire = self.mix || null;
                const fireMul = mixForFire && typeof mixForFire.fireGain === 'number' ? mixForFire.fireGain : 1.0;
                const targetGain = baseGain * fireMul;

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(targetGain, startTime + attack);

                source.connect(envelope);
                if (panner) {
                    envelope.connect(panner);
                    panner.connect(busNode);
                } else {
                    // Non-positional torch loop: envelope goes directly into the bus.
                    envelope.connect(busNode);
                }

                source.start(startTime);

                return { source, envelope, panner, kind, baseGain };
            } catch (e) {
                try { if (source) source.stop(); } catch {}
                return null;
            }
        })();
    }

    /**
     * Internal helper to smoothly stop a looping fire source.
     */
    _stopFireLoop(loopObj, fadeSeconds = 0.25) {
        if (!loopObj || !this.audioContext) return;
        const ac = this.audioContext;
        try {
            const now = ac.currentTime;
            const g = loopObj.envelope && loopObj.envelope.gain;
            if (g && typeof g.cancelScheduledValues === 'function') {
                const current = g.value;
                g.cancelScheduledValues(now);
                g.setValueAtTime(current, now);
                g.linearRampToValueAtTime(0.0001, now + Math.max(0.01, fadeSeconds));
            }
            if (loopObj.source && typeof loopObj.source.stop === 'function') {
                loopObj.source.stop(now + Math.max(0.02, fadeSeconds) + 0.03);
            }
        } catch {
            try {
                if (loopObj.source && typeof loopObj.source.stop === 'function') {
                    loopObj.source.stop();
                }
            } catch {}
        }
    }

    /**
     * Internal helper to move an existing looping fire source.
     */
    _updateFireLoopPosition(loopObj, worldPos) {
        if (!loopObj || !loopObj.panner || !this.audioContext) return;
        if (!Array.isArray(worldPos) || worldPos.length < 3) return;
        const ac = this.audioContext;
        const panner = loopObj.panner;
        const [px, py, pz] = this._screenAlignedPos(
            Number(worldPos[0]) || 0,
            Number(worldPos[1]) || 0,
            Number(worldPos[2]) || 0
        );
        try {
            const t = ac.currentTime;
            if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
                panner.positionX.setValueAtTime(px, t);
                panner.positionY.setValueAtTime(py, t);
                panner.positionZ.setValueAtTime(pz, t);
            } else if (typeof panner.setPosition === 'function') {
                panner.setPosition(px, py, pz);
            }
        } catch {}
    }

    /**
     * Handheld torch one-shot ignition at a world position.
     */
    playTorchIgnite(worldPos) {
        worldPos = this._toVec3(worldPos);
        if (!worldPos) return;
        this._playWorldCategory('world.fire.torch.ignite', worldPos, {
            playbackRateJitter: 0.01,
            maxDuration: 1.5,
            attack: 0.002,
            release: 0.18,
            mixGroup: 'fire'
        }).catch(() => {});
    }

    /**
     * Torch extinguish hiss at a world position (water or unequip).
     */
    playTorchExtinguish(worldPos) {
        worldPos = this._toVec3(worldPos);
        if (!worldPos) return;
        this._playWorldCategory('world.fire.torch.extinguish', worldPos, {
            playbackRateJitter: 0.01,
            maxDuration: 1.8,
            attack: 0.002,
            release: 0.22,
            mixGroup: 'fire'
        }).catch(() => {});
    }

    /**
     * Start (or move) the looping handheld torch fire tied to the player.
     *
     * Uses a request token so that if the torch is extinguished/unequipped while
     * an async loop creation is in flight, the late-arriving loop will be
     * immediately faded out instead of continuing to run forever.
     */
    startTorchLoopAt(worldPos) {
        worldPos = this._toVec3(worldPos);
        if (!worldPos) return;
        if (!this._ensureSfxReady()) return;
        if (this.sfxMuted || this.sfxVolume <= 0) return;

        // If already running, just move it.
        if (this._torchLoop) {
            this._updateFireLoopPosition(this._torchLoop, worldPos);
            return;
        }

        const token = (this._torchLoopRequestToken = (this._torchLoopRequestToken || 0) + 1);
        this._createFireLoop('torch', worldPos, this.worldSfxGain || this.sfxMasterGain)
            .then(loop => {
                if (!loop) return;

                // If another request superseded this one, or a stop was issued,
                // fade out this late loop instead of adopting it.
                if (this._torchLoopRequestToken !== token || this._torchLoop) {
                    try { this._stopFireLoop(loop, 0.15); } catch {}
                    return;
                }

                this._torchLoop = loop;
            })
            .catch(() => {});
    }

    /**
     * Update the handheld torch loop position each frame while lit.
     */
    updateTorchLoopPosition(worldPos) {
        if (!this._torchLoop) return;
        worldPos = this._toVec3(worldPos);
        if (!worldPos) return;
        this._updateFireLoopPosition(this._torchLoop, worldPos);
    }

    /**
     * Stop the handheld torch loop with a short fade.
     */
    stopTorchLoop() {
        // Invalidate any in-flight startTorchLoopAt requests so late-resolving
        // loops do not attach and continue playing after the torch is gone.
        if (typeof this._torchLoopRequestToken === 'number') {
            this._torchLoopRequestToken++;
        }

        if (!this._torchLoop) return;
        this._stopFireLoop(this._torchLoop, 0.25);
        this._torchLoop = null;
    }

    /**
     * Maintain ambient fire loops (campfires / fireplaces) based on visible fireplace lights.
     *
     * @param {Array<{id:string,pos:number[],groundY?:number,radius?:number,isFireplace?:boolean}>} lights
     */
    updateFireplaceLoopsFromLights(lights) {
        const ready = this._ensureSfxReady();

        // If the SFX subsystem or buses are not ready (e.g. audio teardown or a transient
        // AudioContext issue) OR SFX are globally muted/zeroed, proactively shut down any
        // existing fireplace loops. This prevents bonfire loops from outliving the rest of
        // the audio graph and ensures they can never keep playing while all other SFX are
        // silent.
        if (!ready || this.sfxMuted || this.sfxVolume <= 0) {
            if (this._fireplaceLoops && this._fireplaceLoops.size) {
                for (const loop of this._fireplaceLoops.values()) {
                    try { this._stopFireLoop(loop, 0.4); } catch {}
                }
                this._fireplaceLoops.clear();
            }
            if (this._fireplaceLoopTokens && this._fireplaceLoopTokens.size) {
                this._fireplaceLoopTokens.clear();
            }
            return;
        }

        if (!Array.isArray(lights)) lights = [];
        const activeIds = new Set();

        for (const l of lights) {
            if (!l || !l.id || !Array.isArray(l.pos) || l.pos.length < 3) continue;
            if (!l.isFireplace) continue;

            const id = String(l.id);
            activeIds.add(id);

            const basePos = l.pos;
            const gy = Number(l.groundY);
            const worldPos = [
                basePos[0],
                Number.isFinite(gy) ? gy + 0.3 : basePos[1] - 1.0,
                basePos[2]
            ];
 
            // Manual hard range gate for fireplace ambience so they are fully
            // silent beyond their designed radius, independent of PannerNode.
            try {
                if (this.listenerPos && this.listenerPos.length >= 3) {
                    const lp = this.listenerPos;
                    const dx = worldPos[0] - lp[0];
                    const dy = worldPos[1] - lp[1];
                    const dz = worldPos[2] - lp[2];
                    const d = Math.hypot(dx, dy, dz);
 
                    const mixForFire = this.mix || {};
                    const fireDistMul = (typeof mixForFire.fireDistanceMul === 'number')
                        ? mixForFire.fireDistanceMul
                        : 1.0;
                    // Match bonfire base maxDistance (50) as the outer gate and
                    // scale it with the Fire Range slider.
                    const hardRange = 50.0 * fireDistMul;
                    if (d >= hardRange) {
                        const existingLoop = this._fireplaceLoops.get(id);
                        if (existingLoop) {
                            try { this._stopFireLoop(existingLoop, 0.6); } catch {}
                            this._fireplaceLoops.delete(id);
                        }
                        if (this._fireplaceLoopTokens) {
                            this._fireplaceLoopTokens.delete(id);
                        }
                        continue;
                    }
                }
            } catch {}
 
            const existingLoop = this._fireplaceLoops.get(id);
            if (existingLoop) {
                this._updateFireLoopPosition(existingLoop, worldPos);
                continue;
            }

            // Issue a fresh async creation request for this fireplace id and record a token
            // so that slow/duplicated requests cannot leak additional looping sources.
            const token = (this._fireplaceLoopTokens.get(id) || 0) + 1;
            this._fireplaceLoopTokens.set(id, token);

            // Use the large bonfire loop for all fireplaces so they are clearly audible
            // and match the visual size of settlement/wilderness campfires.
            const kind = 'fireplace.bonfire';
            // Prefer the main world SFX bus for fireplaces so they follow the same
            // routing/volume controls as other world sounds. Fall back to ambience
            // or the SFX master if needed.
            const bus = this.worldSfxGain || this.ambienceGain || this.sfxMasterGain;

            this._createFireLoop(kind, worldPos, bus)
                .then(newLoop => {
                    if (!newLoop) return;

                    // If another request superseded this one (token mismatch), immediately
                    // fade out this late-arriving loop instead of adopting it. This prevents
                    // leaked loops that are no longer tracked in _fireplaceLoops.
                    if (this._fireplaceLoopTokens.get(id) !== token) {
                        try { this._stopFireLoop(newLoop, 0.3); } catch {}
                        return;
                    }

                    // If some loop was already attached for this id by the time this resolves,
                    // fade it out before replacing it so we never have two active loops per id.
                    const current = this._fireplaceLoops.get(id);
                    if (current && current !== newLoop) {
                        try { this._stopFireLoop(current, 0.3); } catch {}
                    }

                    this._fireplaceLoops.set(id, newLoop);
                })
                .catch(() => {});
        }

        // If there are no fireplace lights at all this frame, hard-stop all loops.
        if (activeIds.size === 0 && this._fireplaceLoops.size > 0) {
            for (const loop of this._fireplaceLoops.values()) {
                try { this._stopFireLoop(loop, 0.6); } catch {}
            }
            this._fireplaceLoops.clear();
            if (this._fireplaceLoopTokens && this._fireplaceLoopTokens.size) {
                this._fireplaceLoopTokens.clear();
            }
            return;
        }

        // Otherwise, fade out and remove only loops whose lights have disappeared.
        for (const [id, loop] of this._fireplaceLoops.entries()) {
            if (!activeIds.has(id)) {
                this._stopFireLoop(loop, 0.6);
                this._fireplaceLoops.delete(id);
                if (this._fireplaceLoopTokens) {
                    this._fireplaceLoopTokens.delete(id);
                }
            }
        }
    }

    // --- Water movement loops (swimming / underwater) ---------------------------------

    _stopWaterLoopFor(id, fadeSeconds = 0.25) {
        if (!this.audioContext || !id) return;
        const rec = this._waterLoopById && this._waterLoopById.get(id);
        if (!rec || !rec.loop) return;
        const ac = this.audioContext;
        const { source, envelope } = rec.loop;
        try {
            const now = ac.currentTime;
            const g = envelope && envelope.gain;
            if (g && typeof g.cancelScheduledValues === 'function') {
                const current = g.value;
                g.cancelScheduledValues(now);
                g.setValueAtTime(current, now);
                g.linearRampToValueAtTime(0.0001, now + Math.max(0.05, fadeSeconds));
            }
            if (source && typeof source.stop === 'function') {
                source.stop(now + Math.max(0.06, fadeSeconds) + 0.02);
            }
        } catch {
            try { if (source && typeof source.stop === 'function') source.stop(); } catch {}
        }
        rec.loop = null;
        rec.state = 'none';
        this._waterLoopById.set(id, rec);
    }

    _updateWaterLoopFor(id, worldPos) {
        if (!this.audioContext || !id) return;
        const rec = this._waterLoopById && this._waterLoopById.get(id);
        if (!rec || !rec.loop || !rec.loop.panner) return;
        if (!Array.isArray(worldPos) || worldPos.length < 3) return;
        const panner = rec.loop.panner;
        const ac = this.audioContext;
        const [px, py, pz] = this._screenAlignedPos(
            Number(worldPos[0]) || 0,
            Number(worldPos[1]) || 0,
            Number(worldPos[2]) || 0
        );
        try {
            const t = ac.currentTime;
            if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
                panner.positionX.setValueAtTime(px, t);
                panner.positionY.setValueAtTime(py, t);
                panner.positionZ.setValueAtTime(pz, t);
            } else if (typeof panner.setPosition === 'function') {
                panner.setPosition(px, py, pz);
            }
        } catch {}
    }

    async _startWaterLoopFor(id, kind, worldPos, mixGroup) {
        if (!id || !Array.isArray(worldPos) || worldPos.length < 3) return;
        if (!this._ensureSfxReady()) return;
        if (this.sfxMuted || this.sfxVolume <= 0) return;
        if (!this.audioContext || !this.worldSfxGain || !this.sfx) return;

        const ac = this.audioContext;

        // Pick appropriate bank for surface vs fully underwater.
        const urls = (kind === 'underwater')
            ? [
                'audio/sfx/movement/water_underwater_01.wav',
                'audio/sfx/movement/water_underwater_02.wav',
                'audio/sfx/movement/water_underwater_03.wav'
              ]
            : [
                'audio/sfx/movement/water_swimming_01.wav',
                'audio/sfx/movement/water_swimming_02.wav',
                'audio/sfx/movement/water_swimming_03.wav'
              ];
        const url = this._pickRandom(urls);
        if (!url) return;

        const buffer = await this.sfx.loadBuffer(url);
        if (!buffer) return;

        // Stop any existing loop for this id before starting a new one.
        if (this._waterLoopById && this._waterLoopById.has(id)) {
            this._stopWaterLoopFor(id, 0.2);
        }

        let source = null;
        let envelope = null;
        let panner = null;
        try {
            source = ac.createBufferSource();
            envelope = ac.createGain();
            panner = ac.createPanner();

            source.buffer = buffer;
            source.loop = true;

            // Tiny pitch jitter for life
            let rate = 1.0;
            const jitter = 0.01;
            const r = (Math.random() * 2 - 1) * jitter;
            rate *= (1 + r);
            source.playbackRate.value = rate;

            // Routing and distance
            panner.panningModel = 'HRTF';
            panner.distanceModel = 'inverse';

            // Reuse footsteps mix group sliders for water movement.
            const mix = this.mix || {};
            let gainMul = 1.0;
            let distanceMul = 1.0;
            switch (mixGroup) {
                case 'playerFootstep':
                    gainMul = typeof mix.playerFootstepGain === 'number' ? mix.playerFootstepGain : 1.0;
                    distanceMul = typeof mix.footstepDistanceMul === 'number' ? mix.footstepDistanceMul : 1.0;
                    break;
                case 'npcFootstep':
                default:
                    gainMul = typeof mix.npcFootstepGain === 'number' ? mix.npcFootstepGain : 1.0;
                    distanceMul = typeof mix.footstepDistanceMul === 'number' ? mix.footstepDistanceMul : 1.0;
                    break;
            }

            const baseGain = (kind === 'underwater') ? 0.9 : 0.8;
            const targetGain = baseGain * gainMul;

            const refDistance = 3.0;
            const maxBase = (kind === 'underwater') ? 30.0 : 34.0;
            const rolloff = 1.0;

            panner.refDistance = refDistance;
            panner.maxDistance = maxBase * distanceMul;
            panner.rolloffFactor = rolloff;

            const [px, py, pz] = this._screenAlignedPos(
                Number(worldPos[0]) || 0,
                Number(worldPos[1]) || 0,
                Number(worldPos[2]) || 0
            );
            if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
                const t = ac.currentTime;
                panner.positionX.setValueAtTime(px, t);
                panner.positionY.setValueAtTime(py, t);
                panner.positionZ.setValueAtTime(pz, t);
            } else if (typeof panner.setPosition === 'function') {
                panner.setPosition(px, py, pz);
            }

            const now = ac.currentTime;
            const startTime = now + 0.01;
            const attack = 0.12;

            envelope.gain.setValueAtTime(0, startTime);
            envelope.gain.linearRampToValueAtTime(targetGain, startTime + attack);

            source.connect(envelope);
            envelope.connect(panner);
            panner.connect(this.worldSfxGain);

            source.start(startTime);

            const rec = {
                loop: { source, envelope, panner, kind, baseGain },
                state: kind
            };
            this._waterLoopById.set(id, rec);
        } catch {
            try { if (source && typeof source.stop === 'function') source.stop(); } catch {}
        }
    }

    /**
     * Per-character water audio:
     *  - water entry splash (one-shot) when transitioning air -> water,
     *  - surface swim loop while wading/swimming near the surface,
     *  - underwater loop when fully submerged.
     *
     * meta: {
     *   id: string,
     *   worldPos: [x,y,z],
     *   inWater: boolean,
     *   isSwimming: boolean,
     *   submergence: number in [0,1],
     *   velY: number,            // vertical velocity (m/s)
     *   source: 'player' | 'npc',
     *   npcType?: string
     * }
     */
    updateCharacterWaterLoop(meta = {}) {
        if (!meta || !meta.worldPos) return;

        // Accept arrays, TypedArrays, and {x,y,z} objects for worldPos.
        const worldPos = this._toVec3(meta.worldPos);
        if (!worldPos) return;

        const id = (meta.id != null) ? String(meta.id) : String(meta.source || 'water');
        if (!id) return;

        const inWater = !!meta.inWater;
        const isSwimming = !!meta.isSwimming;
        const sub = Number(meta.submergence) || 0;
        const velY = Number(meta.velY) || 0;
        const sourceKind = (meta.source || '').toLowerCase();
        // Optional depth-normalized capsule-top buffer passed by the integrator.
        // When absent, fall back to 1.0 so behaviour matches the legacy thresholds.
        const underDepthFrac = (() => {
            const v = Number(meta.underDepthFrac);
            if (!Number.isFinite(v)) return 1.0;
            return Math.max(0.0, Math.min(1.0, v));
        })();

        const prev = this._waterCharState.get(id) || {
            wasInWater: false,
            lastSplashTime: -Infinity,
            state: 'none'
        };

        const now = this.audioContext
            ? this.audioContext.currentTime
            : (typeof performance !== 'undefined' && performance.now ? performance.now() / 1000 : 0);

        // One-shot entry splash when transitioning from "out of water" to
        // "meaningfully submerged" (not just brushing wave crests).
        // Use a very small submergence gate and rely primarily on downward
        // impact speed + cooldown so:
        //   - jumping/falling into water produces a clear splash,
        //   - standing in shallow, wavy water does NOT spam splashes.
        const entryMinSub = 0.02; // any real intersection with the surface
        const exitMinSub  = 0.01; // hysteresis so tiny submergence does not chatter
        const deepNow     = inWater && sub >= exitMinSub;
        const justEntered = (!prev.wasInWater && inWater && sub >= entryMinSub);

        if (justEntered) {
            const downVel = -velY; // positive when moving downward
            // Splash trigger design:
            //   - Moderate downward impact is enough for shallow/medium water jumps.
            //   - Deep entries (high submergence on first contact) always splash, even if
            //     water damping has already eaten most of the vertical velocity by the
            //     time we see the first "inWater" sample.
            //   - Standing / wading in waves remains quiet thanks to cooldown + wasInWater
            //     hysteresis and the submergence gate above.
            const bigDepth = sub >= 0.7;       // first frame already well submerged
            const velOk   = downVel > 0.6;     // moderate downward speed
            const cooldownOk = (now - (prev.lastSplashTime || -Infinity)) > 0.3;
            const triggerSplash = (velOk || bigDepth) && cooldownOk;
            if (triggerSplash) {
                const mixGroup = (sourceKind === 'player') ? 'playerFootstep' : 'npcFootstep';
                try {
                    this._playWorldCategory('world.water.splash', worldPos, {
                        playbackRateJitter: 0.02,
                        maxDuration: 2.0,
                        attack: 0.01,
                        release: 0.25,
                        mixGroup
                    }).catch(() => {});
                } catch {}
                prev.lastSplashTime = now;
            }
        }
        // Track "in water" with a small hysteresis window so tiny ripples at
        // the ankles do not repeatedly flip the state and spam entry logic.
        prev.wasInWater = deepNow;

        // If SFX are not ready or globally muted, stop any existing loop.
        if (!this._ensureSfxReady() || this.sfxMuted || this.sfxVolume <= 0) {
            this._stopWaterLoopFor(id, 0.2);
            prev.state = 'none';
            this._waterCharState.set(id, prev);
            return;
        }

        // Decide desired loop state from current water state.
        // Underwater should only engage when the capsule top is at least ~20%
        // of its height below the surface (underDepthFrac ~= 1). When only the
        // head briefly dips under waves, we keep using the surface loop.
        // A small hysteresis band avoids chatter around the threshold.
        let desired = 'none';
        if (inWater) {
            const wasUnder = (prev.state === 'underwater');
            const enterUnder = (sub >= 0.99) && (underDepthFrac >= 0.999);     // require true full + buffered submergence
            const stayUnder  = wasUnder && (sub >= 0.9) && (underDepthFrac >= 0.6);
            if (enterUnder || stayUnder) {
                desired = 'underwater';
            } else if (isSwimming) {
                // Only run the surface swimming loop when the controller is
                // actually in its swim state. Pure wading / standing in
                // shallow water should remain silent (no swim loop).
                desired = 'surface';
            }
        }

        const mixGroup = (sourceKind === 'player') ? 'playerFootstep' : 'npcFootstep';

        if (desired !== prev.state) {
            this._stopWaterLoopFor(id, 0.25);
            if (desired !== 'none') {
                this._startWaterLoopFor(id, desired, worldPos, mixGroup);
            }
            prev.state = desired;
        } else if (desired !== 'none') {
            // Keep existing loop following the character.
            this._updateWaterLoopFor(id, worldPos);
        }

        this._waterCharState.set(id, prev);
    }

    /**
     * Update the global AudioListener from a world-space position and orientation.
     * Intended to be called once per frame from the main game loop.
     *
     * @param {number[]} position - [x,y,z]
     * @param {number[]} forward  - [x,y,z]
     * @param {number[]} up       - [x,y,z]
     */
    updateListener(position, forward, up) {
        if (!this.audioContext || !this.audioContext.listener) {
            return;
        }
        const listener = this.audioContext.listener;

        // Accept both plain arrays and TypedArrays (Float32Array, etc.) and
        // coerce to simple [x,y,z] triples. Fall back to safe defaults when
        // input is missing or non-finite.
        const asVec3 = (v, fallback) => {
            if (!v) return fallback;
            if (Array.isArray(v) || ArrayBuffer.isView(v)) {
                const x = Number(v[0]);
                const y = Number(v[1]);
                const z = Number(v[2]);
                if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
                    return [x, y, z];
                }
            }
            // Also accept {x, y, z} objects (trixel coordinate format)
            if (typeof v === 'object' && ('x' in v)) {
                const x = Number(v.x);
                const y = Number(v.y);
                const z = Number(v.z);
                if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
                    return [x, y, z];
                }
            }
            return fallback;
        };

        const p = asVec3(position, [0, 0, 0]);
        const f = asVec3(forward, [0, 0, -1]);

        // Cache listener position for manual distance attenuation / culling.
        try {
            if (this.listenerPos && this.listenerPos.length >= 3) {
                this.listenerPos[0] = p[0];
                this.listenerPos[1] = p[1];
                this.listenerPos[2] = p[2];
            } else {
                this.listenerPos = [p[0], p[1], p[2]];
            }
        } catch {
            this.listenerPos = [p[0], p[1], p[2]];
        }

        // Store camera rotation from forward vector for screen-space panner transforms.
        // forward = [sin(cameraAngle), 0, cos(cameraAngle)]
        this._cameraRot.sin = f[0];
        this._cameraRot.cos = f[2];

        // Set listener position only — orientation stays at default (forward=[0,0,-1], up=[0,1,0]).
        // Panner positions are transformed into screen-aligned coordinates by _screenAlignedPos()
        // to guarantee correct screen-space panning without browser-specific orientation quirks.
        try {
            const t = this.audioContext.currentTime;

            if (listener.positionX && typeof listener.positionX.setValueAtTime === 'function') {
                listener.positionX.setValueAtTime(p[0], t);
                listener.positionY.setValueAtTime(p[1], t);
                listener.positionZ.setValueAtTime(p[2], t);
            } else if (typeof listener.setPosition === 'function') {
                listener.setPosition(p[0], p[1], p[2]);
            }
        } catch {}
    }

    _tempoFromSeed(seed) { return 65 + Math.floor((seed % 1000) / 20); }
    _scaleFromSeed(seed) {
        const scales = ['naturalMinor', 'harmonicMinor', 'dorian', 'phrygian', 'melodicMinor'];
        return scales[Math.abs(seed) % scales.length];
    }

    // ================= Music Controls =================

    async startMusic() {
        if (!this.isInitialized || !this.musicGenerator) {
            this.pendingStart = true;
            return;
        }
        try { await this.musicGenerator.start(); } catch (err) {}
    }

    stopMusic() {
        if (this.musicGenerator) {
            try { this.musicGenerator.stop(); } catch {}
        }
    }

    setMusicVolume(volume) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        if (this.musicGenerator) this.musicGenerator.setVolume(this.musicVolume);
    }

    getMusicVolume() { return this.musicVolume; }

    setSeed(seed) {
        if (this.musicGenerator) {
            const newTempo = this._tempoFromSeed(seed);
            this.musicGenerator.setTempo(newTempo);
            this.musicGenerator.setSeed(seed);
        }
    }

    setTempo(tempo) {
        if (this.musicGenerator) this.musicGenerator.setTempo(tempo);
    }

    isPlaying() {
        return this.musicGenerator ? this.musicGenerator.isActive() : false;
    }

    // ========== SFX API ==========

    setSfxVolume(volume) {
        const v = Number.isFinite(volume) ? volume : 0;
        this.sfxVolume = this._clamp(v, 0, 1);
        if (this._ensureSfxReady()) {
            this._updateSfxGainFromState();
        }
    }

    getSfxVolume() {
        return this.sfxVolume;
    }

    setSfxMuted(muted) {
        this.sfxMuted = !!muted;
        if (this._ensureSfxReady()) {
            this._updateSfxGainFromState();
        }
    }

    isSfxMuted() {
        return !!this.sfxMuted;
    }

    /**
     * Per-UI-bus volume multiplier (0..10) on top of global SFX volume.
     * This lets you turn UI clicks/menu sounds up or down relative to
     * world SFX without touching authored gains.
     */
    setUiSfxVolume(multiplier) {
        const raw = Number(multiplier);
        if (!Number.isFinite(raw)) return;
        const v = this._clamp(raw, 0.0, 10.0);
        this.uiGain = v;

        if (!this._ensureSfxReady() || !this.uiBusGain || !this.audioContext) {
            // Buses not ready yet; value will be applied when _initSfxBuses runs.
            return;
        }

        try {
            const t = this.audioContext.currentTime;
            if (this.uiBusGain.gain.cancelScheduledValues && this.uiBusGain.gain.setTargetAtTime) {
                this.uiBusGain.gain.cancelScheduledValues(t);
                this.uiBusGain.gain.setTargetAtTime(v, t, 0.05);
            } else {
                this.uiBusGain.gain.value = v;
            }
        } catch {
            this.uiBusGain.gain.value = v;
        }
    }

    getUiSfxVolume() {
        return this.uiGain;
    }

    /**
     * Update a single world SFX mix parameter (gain or distance multiplier) at runtime.
     * Keys correspond to this.mix fields: playerFootstepGain, npcFootstepGain, combatGain,
     * npcVoiceGain, fireGain, footstepDistanceMul, combatDistanceMul, npcVoiceDistanceMul,
     * fireDistanceMul.
     */
    setWorldMixParam(key, value) {
        if (!key || typeof key !== 'string') return;
        const k = key;
        if (!this.mix) {
            this.mix = {
                playerFootstepGain: 1.0,
                npcFootstepGain: 1.0,
                combatGain: 1.0,
                npcVoiceGain: 1.0,
                fireGain: 1.0,
                footstepDistanceMul: 1.0,
                combatDistanceMul: 1.0,
                npcVoiceDistanceMul: 1.0,
                fireDistanceMul: 1.0
            };
        }
        const num = Number(value);
        if (!Number.isFinite(num)) return;

        // Gains: [0, 10]; distance multipliers: [0.1, 4].
        // Extra headroom lets you really push specific groups (e.g. footsteps)
        // without having to re-author base palette gains.
        let v = num;
        const isDistance = k.endsWith('DistanceMul');
        if (isDistance) {
            v = this._clamp(v, 0.1, 4.0);
        } else {
            v = this._clamp(v, 0.0, 10.0);
        }

        if (Object.prototype.hasOwnProperty.call(this.mix, k)) {
            this.mix[k] = v;
        } else {
            // Allow future extension without throwing.
            this.mix[k] = v;
        }

        // Fire mix parameters also affect existing torch / fireplace loops.
        if (k === 'fireGain' || k === 'fireDistanceMul') {
            this._applyFireMixToActiveLoops();
        }
    }

    /**
     * Apply current fireGain / fireDistanceMul multipliers to any active looping
     * torch / fireplace sources.
     */
    _applyFireMixToActiveLoops() {
        if (!this.audioContext) return;
        const ac = this.audioContext;
        const mix = this.mix || {};
        const fireGain = (typeof mix.fireGain === 'number') ? mix.fireGain : 1.0;
        const fireDistMul = (typeof mix.fireDistanceMul === 'number') ? mix.fireDistanceMul : 1.0;

        const applyToLoop = (loop) => {
            if (!loop || !loop.envelope) return;
            const g = loop.envelope.gain;
            const baseGain = (typeof loop.baseGain === 'number') ? loop.baseGain : 1.0;
            const targetGain = baseGain * fireGain;
            try {
                const now = ac.currentTime;
                if (typeof g.cancelScheduledValues === 'function' && typeof g.setTargetAtTime === 'function') {
                    g.cancelScheduledValues(now);
                    g.setTargetAtTime(targetGain, now, 0.15);
                } else {
                    g.value = targetGain;
                }
            } catch {}

            if (loop.panner && loop.kind && loop.kind !== 'torch') {
                let refBase = 3.0;
                let maxBase = 32.0;
                let rollBase = 1.1;
                if (loop.kind === 'fireplace.brazier') {
                    refBase = 2.0;
                    maxBase = 40.0;
                    rollBase = 3.0;
                } else if (loop.kind === 'fireplace.bonfire') {
                    refBase = 3.0;
                    maxBase = 50.0;
                    rollBase = 2.5;
                }
                try {
                    // Keep the near-field shape anchored while letting fireDistanceMul
                    // control only how far the loop carries.
                    loop.panner.refDistance = refBase;
                    loop.panner.maxDistance = maxBase * fireDistMul;
                    loop.panner.rolloffFactor = rollBase;
                } catch {}
            }
        };

        if (this._torchLoop) {
            applyToLoop(this._torchLoop);
        }
        if (this._fireplaceLoops && this._fireplaceLoops.size) {
            for (const loop of this._fireplaceLoops.values()) {
                applyToLoop(loop);
            }
        }
    }

    /**
     * Generic SFX entry point. For now we route only UI categories through
     * the UI bus; future world/ambience categories can map to worldSfx/ambience buses.
     *
     * @param {string} categoryId e.g. "ui.click.light"
     * @param {Object} [options]
     */
    playEffect(categoryId, options = {}) {
        if (!categoryId) return;
        const id = String(categoryId);
        if (id.startsWith('ui.')) {
            this._playUiCategory(id, options);
        }
        // Non-UI categories can be added later
    }

    // Convenience helpers for UI sounds
    playUiClick()   { this.playEffect('ui.click'); }
    playUiConfirm() { this.playEffect('ui.confirm'); }
    playUiCancel()  { this.playEffect('ui.cancel'); }
    playUiOpen()    { this.playEffect('ui.open'); }
    playUiClose()   { this.playEffect('ui.close'); }
    playUiFanfare() { this.playEffect('ui.fanfare'); }
    playUiDialog()  { this.playEffect('ui.dialog'); }
    playPlayerDrink() { this.playEffect('ui.player.drink'); }

    // ========== Utilities ==========

    _clamp(x, a, b) { return Math.max(a, Math.min(b, x)); }
    _weightFromMass(mass) {
        const w = Math.log10(Math.max(0.1, mass) + 1) * 3.2 + 0.2;
        return this._clamp(w, 0.1, 5);
    }
    _materialFromColor(color) { return 'stone'; }

    cleanup() {
        try { if (this.musicGenerator) this.musicGenerator.stop(); } catch {}
        this.isInitialized = false;
        this.musicGenerator = null;

        // Ensure any lingering looping fire sources are faded out when the audio
        // system is torn down (e.g. leaving the world / reloading).
        try {
            if (this._torchLoop) {
                this._stopFireLoop(this._torchLoop, 0.2);
                this._torchLoop = null;
            }
            if (this._fireplaceLoops && this._fireplaceLoops.size) {
                for (const loop of this._fireplaceLoops.values()) {
                    try { this._stopFireLoop(loop, 0.4); } catch {}
                }
                this._fireplaceLoops.clear();
            }
        } catch {}
    }
}
